import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDate } from '../utils/dateFormat';
import {
  ShieldCheck, ShieldAlert, Pin, PinOff, RefreshCw, Loader2, Search, EyeOff, TrendingDown, DollarSign,
  PackageX, AlertTriangle, ShoppingCart, Settings2
} from 'lucide-react';
import { useStore } from '../context/StoreContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const StatusBadge = ({ status }) => {
  if (status === 'out') return <Badge className="bg-rose-600 text-white">OUT</Badge>;
  if (status === 'low') return <Badge className="bg-amber-500 text-white">LOW</Badge>;
  return <Badge className="bg-emerald-600 text-white">OK</Badge>;
};

const Stat = ({ label, value, sub, tone, icon: Icon, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className={`text-2xl font-bold ${tone || ''}`}>{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
      <Icon className={`w-8 h-8 ${tone || 'text-slate-400'}`} />
    </CardContent>
  </Card>
);

const PinSearch = ({ onPin }) => {
  const { getAuthHeader } = useAuth();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API_URL}/products?search=${encodeURIComponent(q.trim())}&limit=8`, { headers: getAuthHeader() })
        .then(res => setResults(res.data || [])).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, getAuthHeader]);
  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-slate-400" />
        <Input placeholder="Pin a product to never-out (e.g. Solgar Glucosamine)…" value={q} onChange={e => setQ(e.target.value)} data-testid="never-out-pin-search" />
      </div>
      {results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid="never-out-pin-results">
          {results.map(p => (
            <button key={p.id} type="button" onClick={() => { onPin(p); setQ(''); setResults([]); }}
              className="w-full text-left p-2 hover:bg-slate-100 flex justify-between items-center text-sm" data-testid={`never-out-pin-result-${p.id}`}>
              <span><span className="font-medium">{p.name}</span> <span className="text-slate-400">{p.supplier_name || ''}</span></span>
              <span className="text-slate-500">stock {p.quantity ?? 0}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SettingsBar = ({ settings, onSaved }) => {
  const { getAuthHeader } = useAuth();
  const [draft, setDraft] = useState({ lead_time_days: settings.lead_time_days, dead_days: settings.dead_days, top_percent: settings.top_percent, cover_weeks: settings.cover_weeks, window_days: settings.window_days });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/inventory-health/settings`, draft, { headers: getAuthHeader() });
      onSaved(res.data);
      toast.success('Settings saved — recalculating');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };
  const field = (key, label, suffix) => (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-slate-600">{label}</span>
      <Input type="number" min="1" className="w-20 h-8" value={draft[key]} onChange={e => setDraft({ ...draft, [key]: parseInt(e.target.value) || 1 })} data-testid={`ih-setting-${key}`} />
      <span className="text-slate-500">{suffix}</span>
    </label>
  );
  return (
    <Card data-testid="ih-settings">
      <CardContent className="p-4 flex flex-wrap items-center gap-4">
        <Settings2 className="w-4 h-4 text-slate-500" />
        {field('window_days', 'Sales history used', 'days')}
        {field('lead_time_days', 'Supplier delivery', 'days')}
        {field('cover_weeks', 'Order enough for', 'weeks')}
        {field('top_percent', 'Never-out = top', '% of sellers')}
        {field('dead_days', 'Dead stock after', 'days without a sale')}
        <Button size="sm" onClick={save} disabled={saving} data-testid="ih-settings-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
      </CardContent>
    </Card>
  );
};

export default function InventoryHealth() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('attention');
  const [showSettings, setShowSettings] = useState(false);
  const [runningAlerts, setRunningAlerts] = useState(false);
  const [view, setView] = useState('all');
  const { storeName, stores } = useStore();
  const STORE_LABEL = { orewa: storeName('orewa'), eastridge: storeName('eastridge') };

  const load = useCallback(async (refresh = false) => {
    try {
      const res = await axios.get(`${API_URL}/inventory-health${refresh ? '?refresh=1' : ''}`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (e) {
      toast.error('Could not load inventory health');
    } finally { setLoading(false); setRefreshing(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const act = async (method, path, okMsg) => {
    try {
      const res = await axios({ method, url: `${API_URL}${path}`, headers: getAuthHeader() });
      setData(res.data);
      toast.success(okMsg);
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const runAlerts = async () => {
    setRunningAlerts(true);
    try {
      const res = await axios.post(`${API_URL}/inventory-health/run-alerts`, {}, { headers: getAuthHeader() });
      toast.success(res.data.out || res.data.low ? `Alert sent — ${res.data.out} out, ${res.data.low} low` : 'Nothing to alert — all never-out items are stocked');
      load();
    } catch (e) { toast.error('Could not run alerts'); } finally { setRunningAlerts(false); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const order = { out: 0, low: 1, ok: 2, na: 3 };
    let list = data.never_out;
    if (view !== 'all') {
      list = list
        .map(x => ({ ...x, ...x.stores[view], stock_shown: x.stores[view].stock }))
        .filter(x => x.status !== 'na')
        .sort((a, b) => order[a.status] - order[b.status] || b.revenue_window - a.revenue_window);
    }
    if (filter === 'attention') return list.filter(x => x.status !== 'ok');
    if (filter === 'pinned') return list.filter(x => x.pinned);
    return list;
  }, [data, filter, view]);

  const dead = useMemo(() => {
    if (!data) return { list: [], bySupplier: [] };
    if (view === 'all') return { list: data.dead, bySupplier: data.dead_by_supplier };
    return { list: data.dead_by_store?.[view] || [], bySupplier: data.dead_by_supplier_by_store?.[view] || [] };
  }, [data, view]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  if (!data) return null;
  const s = view === 'all' ? data.summary : { ...data.summary, ...(data.summary_by_store?.[view] || {}) };

  return (
    <div className="space-y-6" data-testid="inventory-health-page">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" /> Inventory Health
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Your top sellers watched daily, true sales speed, and the stock that just sits there. Built from the last {Math.round(data.settings.window_days / 30)} months of your own till sales, counting only stock physically in Orewa or Eastridge — online-only products ({data.summary.online_only_excluded || 0}) are left out. No AI credits.
            <span className="ml-2 text-xs text-slate-400">Updated {formatDate(data.computed_at)}</span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex rounded-md border overflow-hidden" data-testid="ih-store-toggle">
            {[['all', 'Both stores'], ...stores.map(s => [s.code, s.name])].filter(([k]) => k === 'all' ? stores.length > 1 : true).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setView(k)}
                className={`px-3 py-2 text-sm font-medium ${view === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid={`ih-store-${k}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" onClick={() => setShowSettings(v => !v)} data-testid="ih-toggle-settings"><Settings2 className="w-4 h-4 mr-2" /> Settings</Button>
          <Button variant="outline" onClick={runAlerts} disabled={runningAlerts} data-testid="ih-run-alerts">{runningAlerts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />} Send alert now</Button>
          <Button onClick={() => { setRefreshing(true); load(true); }} disabled={refreshing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ih-refresh">
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Recalculate
          </Button>
        </div>
      </div>

      {showSettings && <SettingsBar settings={data.settings} onSaved={() => setTimeout(() => load(), 1500)} />}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Never-out items" value={s.a_items} sub={`top ${data.settings.top_percent}% of ${s.sold_skus_window} sellers + pinned`} icon={ShieldCheck} tone="text-emerald-700" testId="ih-stat-a" />
        <Stat label="Out of stock" value={s.out} sub={s.lost_sales ? `≈${money(s.lost_sales)} lost sales` : 'all stocked'} icon={PackageX} tone={s.out ? 'text-rose-600' : 'text-emerald-700'} testId="ih-stat-out" />
        <Stat label="Running low" value={s.low} sub={`under ${data.settings.lead_time_days} days of cover`} icon={AlertTriangle} tone={s.low ? 'text-amber-600' : 'text-emerald-700'} testId="ih-stat-low" />
        <Stat label="Dead stock lines" value={s.dead_items} sub={`${s.dead_units} units, no sale in ${data.settings.dead_days} days`} icon={TrendingDown} tone="text-slate-700" testId="ih-stat-dead" />
        <Stat label="Cash sitting on shelf" value={money(s.dead_cash)} sub="at buy price" icon={DollarSign} tone="text-slate-700" testId="ih-stat-cash" />
      </div>

      <Tabs defaultValue="never-out">
        <TabsList>
          <TabsTrigger value="never-out" data-testid="ih-tab-never-out">Never-Out watch</TabsTrigger>
          <TabsTrigger value="dead" data-testid="ih-tab-dead">Dead stock</TabsTrigger>
        </TabsList>

        <TabsContent value="never-out" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Top sellers that must never run out{view !== 'all' ? ` — ${STORE_LABEL[view]}` : ''}</CardTitle>
                  <CardDescription>Ranked by money earned, profit and units over the last {Math.round(data.settings.window_days / 30)} months. "Sells/wk" only counts weeks the item was actually on the shelf, so an item that's been out for a month still gets ordered properly.</CardDescription>
                </div>
                <div className="flex gap-1">
                  {[['attention', 'Needs attention'], ['all', 'All'], ['pinned', 'Pinned']].map(([k, l]) => (
                    <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} onClick={() => setFilter(k)} data-testid={`ih-filter-${k}`}>{l}</Button>
                  ))}
                </div>
              </div>
              <div className="pt-3"><PinSearch onPin={(p) => act('post', `/inventory-health/pin/${p.id}`, `${p.name} pinned to never-out`)} /></div>
            </CardHeader>
            <CardContent>
              {rows.length === 0 ? (
                <p className="text-center text-slate-500 py-8" data-testid="ih-never-out-empty">{filter === 'attention' ? 'Every never-out item is in stock. 🎉' : 'No items.'}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">{view === 'all' ? 'Stock Ore / East' : `Stock ${STORE_LABEL[view]}`}</TableHead>
                      <TableHead className="text-center">{view === 'all' ? 'Sells / wk' : `Sells / wk ${STORE_LABEL[view]}`}</TableHead>
                      <TableHead className="text-center">Days cover</TableHead>
                      <TableHead className="text-right">Revenue ({Math.round(data.settings.window_days / 30)}m)</TableHead>
                      <TableHead className="text-center">{view === 'all' ? 'Order now' : `${STORE_LABEL[view]} order`}</TableHead>
                      <TableHead className="text-center"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map(x => (
                      <TableRow key={x.product_id} className={x.status === 'out' ? 'bg-rose-50/60' : x.status === 'low' ? 'bg-amber-50/60' : ''} data-testid={`ih-row-${x.product_id}`}>
                        <TableCell>
                          <div className="font-medium flex items-center gap-1">{x.pinned && <Pin className="w-3 h-3 text-emerald-600" />}{x.name}</div>
                          {x.status === 'out' && <p className="text-xs text-rose-700">out {x.days_out ?? '?'} days · ≈{money(x.lost_sales)} lost · last sold {x.last_sale ? formatDate(x.last_sale) : '-'}</p>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{x.supplier || '-'}</TableCell>
                        <TableCell className="text-center"><StatusBadge status={x.status} /></TableCell>
                        <TableCell className="text-center font-mono text-sm">{view === 'all' ? `${x.stock_orewa} / ${x.stock_eastridge}` : x.stock_shown}</TableCell>
                        <TableCell className="text-center">{x.per_week}</TableCell>
                        <TableCell className="text-center">{x.days_cover === null ? '-' : x.days_cover}</TableCell>
                        <TableCell className="text-right">{money(x.revenue_window)}</TableCell>
                        <TableCell className="text-center font-bold">{x.suggested_order > 0 ? x.suggested_order : '-'}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1">
                            {x.pinned
                              ? <Button size="sm" variant="ghost" title="Unpin" onClick={() => act('delete', `/inventory-health/pin/${x.product_id}`, 'Unpinned')} data-testid={`ih-unpin-${x.product_id}`}><PinOff className="w-4 h-4" /></Button>
                              : <Button size="sm" variant="ghost" title="Pin — always watch this product" onClick={() => act('post', `/inventory-health/pin/${x.product_id}`, 'Pinned to never-out')} data-testid={`ih-pin-${x.product_id}`}><Pin className="w-4 h-4" /></Button>}
                            <Button size="sm" variant="ghost" title="Stop watching this product" onClick={() => act('post', `/inventory-health/ignore/${x.product_id}`, 'No longer watched')} data-testid={`ih-ignore-${x.product_id}`}><EyeOff className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {rows.some(x => x.suggested_order > 0) && (
                <div className="mt-4 flex justify-end">
                  <Button onClick={() => navigate('/smart-po')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ih-go-smart-po"><ShoppingCart className="w-4 h-4 mr-2" /> Order in Smart PO (⭐ items sit at the top)</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dead" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 items-start">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Stock that just sits there{view !== 'all' ? ` — ${STORE_LABEL[view]}` : ''}</CardTitle>
                <CardDescription>In stock but no sale for {data.settings.dead_days}+ days{view !== 'all' ? ` at ${STORE_LABEL[view]}` : ''}. Smart PO will no longer top these up. Mark down, return to the supplier, transfer to the other store, or let them sell through.</CardDescription>
              </CardHeader>
              <CardContent>
                {dead.list.length === 0 ? <p className="text-center text-slate-500 py-8">No dead stock. 🎉</p> : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead className="text-center">{view === 'all' ? 'Stock Ore / East' : `Stock ${STORE_LABEL[view]}`}</TableHead>
                        <TableHead className="text-center">{view === 'all' ? 'Last sale' : `Last sale at ${STORE_LABEL[view]}`}</TableHead>
                        <TableHead className="text-right">Cash tied</TableHead>
                        <TableHead className="text-center"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dead.list.slice(0, 200).map(d => (
                        <TableRow key={d.product_id} data-testid={`ih-dead-${d.product_id}`}>
                          <TableCell className="font-medium">{d.name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{d.supplier || '-'}</TableCell>
                          <TableCell className="text-center font-mono text-sm">{view === 'all' ? `${d.stock_orewa} / ${d.stock_eastridge}` : d.stock}</TableCell>
                          <TableCell className="text-center text-sm">{d.last_sale ? `${formatDate(d.last_sale)} (${d.days_since_sale}d)` : 'never'}</TableCell>
                          <TableCell className="text-right font-semibold">{money(d.cash_tied)}</TableCell>
                          <TableCell className="text-center">
                            <Button size="sm" variant="ghost" title="Actually a must-have — keep ordering it" onClick={() => act('post', `/inventory-health/pin/${d.product_id}`, `${d.name} pinned — will be reordered`)} data-testid={`ih-dead-pin-${d.product_id}`}><Pin className="w-4 h-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {dead.list.length > 200 && <p className="text-xs text-slate-500 mt-2">Showing top 200 by cash tied up of {dead.list.length}.</p>}
              </CardContent>
            </Card>
            <Card data-testid="ih-dead-by-supplier">
              <CardHeader className="pb-3"><CardTitle className="text-base">By supplier</CardTitle><CardDescription>Where the cash is stuck</CardDescription></CardHeader>
              <CardContent className="space-y-2">
                {dead.bySupplier.slice(0, 15).map(g => (
                  <div key={g.supplier} className="flex justify-between text-sm border-b pb-1">
                    <span className="truncate pr-2">{g.supplier} <span className="text-slate-400">({g.items})</span></span>
                    <span className="font-semibold shrink-0">{money(g.cash_tied)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
